import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { CellProps, Column } from 'react-table';
import { toast } from 'react-toastify';
import Breadcrumbs from '~/components/Breadcrumbs/Breadcrumbs';
import CobuTableAdvanced from '~/components/CobuTableAdvanced/CobuTableAdvanced';
import CobuSelect from '~/components/CobuSelect/CobuSelect';
import GenericModal from '~/components/GenericModal/GenericModal';
import Label from '~/components/Label/index';
import Spinner from '~/components/Spinner/Spinner';
import ToggleSwitch from '~/components/ToggleSwitch/ToggleSwitch';
import { SelectType } from '~/enums/SelectType';
import { useBuilding } from '~/hooks/building/useBuilding';
import { SelectedOptionType } from '~/types/form';
import {
  createPlace,
  fetchPlaces,
  Place,
  PlaceCategory,
  updatePlace
} from '~/api/places';
import AddPlaceForm from './AddPlaceForm';
import {
  categoryIcon,
  categoryLabel,
  categoryOptions,
  toCategory
} from './constants';
import { IAddPlaceForm } from './interfaces';
import classes from './Places.module.scss';

export const Places = () => {
  const { buildingId } = useParams<{ buildingId: string }>();
  const { data: building } = useBuilding(buildingId);
  const queryClient = useQueryClient();

  const [showAddModal, setShowAddModal] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<PlaceCategory | ''>('');

  const placesQueryKey = ['places', buildingId];

  const { data: places = [], isLoading } = useQuery(placesQueryKey, () =>
    fetchPlaces(buildingId)
  );

  const createMutation = useMutation(
    (form: IAddPlaceForm) =>
      createPlace(buildingId, {
        name: form.name,
        category: form.category,
        mapsLink: form.mapsLink,
        street: form.street,
        city: form.city,
        zip: form.zip,
        description: form.description
      }),
    {
      onSuccess: () => {
        toast.success('Place added');
        setShowAddModal(false);
        queryClient.invalidateQueries(placesQueryKey);
      },
      onError: () => {
        toast.error('Could not add place');
      }
    }
  );

  const toggleVisibilityMutation = useMutation(
    ({ place, visible }: { place: Place; visible: boolean }) =>
      updatePlace(buildingId, place.uuid, { isClosed: !visible }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(placesQueryKey);
      },
      onError: () => {
        toast.error('Could not update visibility');
      }
    }
  );

  const filteredPlaces = useMemo(
    () =>
      categoryFilter
        ? places.filter((p) => p.category === categoryFilter)
        : places,
    [places, categoryFilter]
  );

  const visibleCount = filteredPlaces.filter((p) => !p.isClosed).length;
  const hiddenCount = filteredPlaces.length - visibleCount;
  const reportedCount = filteredPlaces.filter(
    (p) => (p.reportCount ?? 0) > 0
  ).length;

  const columns = useMemo<Column<Place>[]>(
    () => [
      {
        Header: 'PLACE',
        accessor: 'name',
        Cell: (cell: CellProps<Place>) => {
          const place = cell.row.original;
          return (
            <div className={classes.placeCell}>
              <div className={classes.placeIcon}>
                {categoryIcon(place.category)}
              </div>
              <div>
                <div className={classes.placeName}>{place.name}</div>
                <div className={classes.placeAddress}>
                  {place.street || place.city || place.zip ? (
                    [place.street, place.city, place.zip]
                      .filter(Boolean)
                      .join(', ')
                  ) : place.mapsLink ? (
                    <a
                      href={place.mapsLink}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      View on Google Maps
                    </a>
                  ) : null}
                </div>
              </div>
            </div>
          );
        }
      },
      {
        Header: 'LIKES',
        id: 'likes',
        Cell: (cell: CellProps<Place>) => (
          <span className={classes.count}>
            {cell.row.original.likeCount ?? 0}
          </span>
        )
      },
      {
        Header: 'BEEN',
        id: 'been',
        Cell: (cell: CellProps<Place>) => (
          <span className={classes.count}>
            {cell.row.original.beenCount ?? 0}
          </span>
        )
      },
      {
        Header: 'SHARES',
        id: 'shares',
        Cell: (cell: CellProps<Place>) => (
          <span className={classes.count}>
            {cell.row.original.shareCount ?? 0}
          </span>
        )
      },
      {
        Header: 'COMMENTS',
        id: 'comments',
        Cell: (cell: CellProps<Place>) => (
          <span className={classes.count}>
            {cell.row.original.commentCount ?? 0}
          </span>
        )
      },
      {
        Header: 'REPORTED',
        id: 'reported',
        Cell: (cell: CellProps<Place>) => {
          const count = cell.row.original.reportCount ?? 0;
          if (!count) return <span className={classes.count}>—</span>;
          return (
            <Label
              title={`${count} report${count === 1 ? '' : 's'}`}
              style={{ backgroundColor: '#ffe1e1', color: '#a12b2b' }}
            />
          );
        }
      },
      {
        Header: 'VISIBLE TO RESIDENTS',
        id: 'visibility',
        Cell: (cell: CellProps<Place>) => {
          const place = cell.row.original;
          const visible = !place.isClosed;
          return (
            <div className={classes.visibilityCell}>
              <Label
                title={visible ? 'Visible' : 'Hidden'}
                style={{
                  backgroundColor: visible ? '#e6f4ea' : '#e8e8ee',
                  color: visible ? '#1e7e46' : '#6b6b7b'
                }}
              />
              <ToggleSwitch
                checked={visible}
                disabled={toggleVisibilityMutation.isLoading}
                onChange={(checked) =>
                  toggleVisibilityMutation.mutate({ place, visible: checked })
                }
              />
            </div>
          );
        }
      }
    ],
    [toggleVisibilityMutation]
  );

  const addPlaceAction = (
    <button
      className='btn-primary btn-standard bold'
      onClick={() => setShowAddModal(true)}
    >
      + Add place
    </button>
  );

  return (
    <div className='content-container'>
      <GenericModal
        showModal={showAddModal}
        title='Add a place'
        width='520px'
        hideActionButton
        canceActionButton
        onChange={setShowAddModal}
        content={
          // GenericModal keeps `content` mounted even while hidden, so without this
          // check AddPlaceForm's useForm() state (and whatever was last typed) never
          // resets between opens.
          showAddModal ? (
            <AddPlaceForm
              submitting={createMutation.isLoading}
              cancel={() => setShowAddModal(false)}
              onSubmit={(form) => createMutation.mutate(form)}
            />
          ) : null
        }
      />
      {isLoading && <Spinner />}
      {!isLoading && (
        <>
          <Breadcrumbs />
          <div className={classes.header}>
            <h1 className={classes.title}>Places</h1>
            <p className={classes.subtitle}>
              Moderate resident-added places for <b>{building?.name}</b>.
            </p>
          </div>
          <div className={classes.toolbar}>
            <CobuSelect
              selectType={SelectType.Filter}
              isClearable
              placeholder='All categories'
              defaultValue={
                categoryFilter
                  ? {
                      value: categoryFilter,
                      label: categoryLabel(categoryFilter)
                    }
                  : null
              }
              options={categoryOptions}
              onChange={(option: SelectedOptionType | null) => {
                setCategoryFilter(toCategory(option?.value));
              }}
            />
            <span className={classes.count}>
              {filteredPlaces.length} total · {visibleCount} visible ·{' '}
              {hiddenCount} hidden · {reportedCount} reported
            </span>
            {addPlaceAction}
          </div>
          <CobuTableAdvanced
            data={filteredPlaces}
            columns={columns}
            noData={{ label: 'No places yet' }}
          />
        </>
      )}
    </div>
  );
};

export default Places;
